import { createFocusTrap } from "focus-trap";
import { add, listen, qs, remove } from "@fluorescent/dom";
import { formatMoney } from "@/neon";

import { emit, on } from "@/neon/events";
import { getSizedImageUrl, imageSize } from "@/lib/images";
import { renderUnitPrice } from "@/lib/product";
import { animateStoreAvailability } from "@/lib/animation";

const classes = {
  visible: "is-visible",
};

const selectors = {
  closeBtn: "[data-store-availability-close]",
  productTitle: "[data-store-availability-product-title]",
  variantTitle: "[data-store-availability-variant-title]",
  productCard: "[data-store-availability-product]",
  storeListcontainer: "[data-store-list-container]",
};

const storeAvailabilityDrawer = node => {
  if (!node) return;
  var focusTrap = createFocusTrap(node, { allowOutsideClick: true });
  const productCard = qs(selectors.productCard, node);
  const storeListContainer = qs(selectors.storeListcontainer, node);

  const events = [
    listen(qs(selectors.closeBtn, node), "click", e => {
      e.preventDefault();
      _close();
    }),
    listen(node, "keydown", ({ keyCode }) => {
      if (keyCode === 27) _close();
    }),
    on("drawerOverlay:hiding", () => _close(false)),
    on("availability:showMore", ({ product, variant, storeList }) => {
      productCard.innerHTML = _renderProductCard(product, variant);
      _renderAvailabilityList(storeList);
      _open();
    }),
  ];

  const storeAvailabilityAnimation = animateStoreAvailability(node);

  const _renderAvailabilityList = storeList => {
    storeListContainer.innerHTML = "";
    storeListContainer.appendChild(storeList);
    setTimeout(() => {
      storeAvailabilityAnimation?.setup();
    }, 10);
  };

  const _renderProductCard = (
    { featured_image: image, title },
    {
      title: variant_title,
      featured_image,
      price,
      unit_price,
      unit_price_measurement,
    }
  ) => {
    let productImage = _getVariantImage(image, featured_image);

    return `
      <div class="store-availability-flyout__product-card type-body-regular">
        ${
          productImage
            ? `
            <div class='store-availability-flyout__product-card-image'>
              <img src='${productImage}' alt='${title}'/>
            </div>
          `
            : ""
        }
        <div class='store-availability-flyout__product-card-details'>
          <div>
            <h4 class="ma0">
              <span>${title}</span>
            </h4>
            <div class="store-availability-flyout__product-price-wrapper">
              <span class="store-availability-flyout__product-price">${formatMoney(
                price
              )}</span>
              ${renderUnitPrice(unit_price, unit_price_measurement)}
            </div>
            <div class="store-availability-flyout__product-card-options">
              ${variant_title}
            </div>
          </div>
        </div>
      </div>
    `;
  };

  const _getVariantImage = (productImage, variantImage) => {
    if (!productImage && !variantImage) return "";

    if (variantImage) {
      return _updateImageSize(variantImage.src);
    }

    return _updateImageSize(productImage);
  };

  const _updateImageSize = imageUrl => {
    return getSizedImageUrl(
      imageUrl.replace("." + imageSize(imageUrl), ""),
      "200x"
    );
  };

  const _open = () => {
    add(node, classes.visible);
    node.setAttribute("aria-hidden", "false");
    focusTrap.activate();
    emit("drawerOverlay:show");
    setTimeout(() => {
      storeAvailabilityAnimation?.open();
    }, 10);
  };

  const _close = (hideOverlay = true) => {
    focusTrap.deactivate();
    hideOverlay && emit("drawerOverlay:hide");
    remove(node, classes.visible);
    node.setAttribute("aria-hidden", "true");
    setTimeout(() => {
      storeAvailabilityAnimation?.close();
    }, 10);
  };

  const unload = () => {
    events.forEach(unsubscribe => unsubscribe());
  };

  return { unload };
};

export default storeAvailabilityDrawer;
